import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Container,
  Fab,
  Fade,
  Tooltip,
  LinearProgress,
} from "@mui/material";
import {
  PlayArrowRounded,
  PauseRounded,
  ArrowBackIosNewRounded,
} from "@mui/icons-material";
// relaxation_video.mp4 is not present in the repo; fall back to API-provided video only
const defaultVideo = null;
import { completeRelaxation } from "services/relaxation/completeRelaxation";
import { createRelaxationReport } from "services/relaxationReport/createRelaxationReport";
import toast from "react-hot-toast";

const RelaxationTracking = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const audioRef = useRef(null);
  const videoRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const initialState = location.state || {};

  const {
    courseId,
    isContinuousFlow = false,
    allRelaxations = [],
    sequenceType,
  } = initialState;

  const [currentIndex, setCurrentIndex] = useState(
    initialState.currentIndex ?? 0
  );

  const [currentItem, setCurrentItem] = useState(() => {
    if (
      isContinuousFlow &&
      allRelaxations.length > 0 &&
      initialState.currentIndex != null
    ) {
      return allRelaxations[initialState.currentIndex];
    }

    return {
      id: initialState.selectedId,
      track_name: initialState.name || "Relaxation Session",
      audio: initialState.audio,
      video: initialState.video,
      duration: initialState.duration,
      instructions: initialState.instructions,
    };
  });

  const roleid = localStorage.getItem("roleid");
  const rawStudentId = localStorage.getItem("studentid");
  const rawUserId = localStorage.getItem("userid");
  // Staff (role 5) don't have studentid — use userid instead
  const studentId = Number(roleid === "5" || !rawStudentId ? rawUserId : rawStudentId);


  // ✅ FIXED SOURCE MAPPING
  const audioSrc =
    currentItem?.audio ||
    currentItem?.audio_file ||
    null;

  const videoSrc =
    currentItem?.video ||
    currentItem?.video_file ||
    defaultVideo;

  const formatTime = (time) => {
    if (isNaN(time)) return "00:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const progressPercentage =
    duration > 0 ? (currentTime / duration) * 100 : 0;

  const completeMutation = useMutation({
    mutationFn: completeRelaxation,
    onSuccess: () => {
      toast.success("Relaxation completed!");
      queryClient.invalidateQueries({
        queryKey: ["relaxations", courseId],
      });
    },
    onError: (err) => {
      toast.error("Failed to mark as completed");
      console.error(err);
    },
  });

  const createReportMutation = useMutation({
    mutationFn: createRelaxationReport,
    onSuccess: (data) => {
      toast.success(data.message || "Report created!");
      queryClient.invalidateQueries({
        queryKey: ["relaxation"],
      });
    },
    onError: (err) => {
      toast.error("Failed to create report");
      console.error(err);
    },
  });

  // ✅ RESET WHEN SOURCE CHANGES (NO MANUAL SRC OVERRIDE)
  useEffect(() => {
    const audioEl = audioRef.current;
    const videoEl = videoRef.current;

    if (!audioEl) return;

    audioEl.pause();
    if (videoEl) videoEl.pause();

    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
  }, [audioSrc, videoSrc]);

  // ✅ AUTOPLAY SAFE
  useEffect(() => {
    if (!audioRef.current || !audioSrc) return;

    const playPromise = audioRef.current.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          setIsPlaying(true);
        })
        .catch((err) => {
          console.warn("Autoplay blocked:", err);
          setIsPlaying(false);
        });
    }

    if (videoRef.current && videoSrc) {
      videoRef.current.play().catch(() => {});
    }
  }, [audioSrc, videoSrc]);

  const handlePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
      videoRef.current?.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().catch(() => {});
      videoRef.current?.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration || 0);
    }
  };

  const goToNext = () => {
    if (!isContinuousFlow) {
      navigate(-1);
      return;
    }

    const nextIndex = currentIndex + 1;

    if (nextIndex >= allRelaxations.length) {
      toast.success("All relaxations in this flow completed!");
      navigate(-1);
      return;
    }

    setCurrentIndex(nextIndex);
    setCurrentItem(allRelaxations[nextIndex]);
  };

  useEffect(() => {
    const audioElement = audioRef.current;
    if (!audioElement || !audioSrc) return;

    const handleAudioEnd = () => {
      setIsPlaying(false);

      if (studentId && courseId && currentItem?.id) {
        const payload = {
          studentId,
          courseId: Number(courseId),
          relaxationId: Number(currentItem.id),
        };

        completeMutation.mutate(payload);
        createReportMutation.mutate(payload);
      }

      setTimeout(() => {
        goToNext();
      }, 1200);
    };

    audioElement.addEventListener("ended", handleAudioEnd);

    return () => {
      audioElement.removeEventListener("ended", handleAudioEnd);
    };
  }, [
    audioSrc,
    studentId,
    courseId,
    currentItem,
    isContinuousFlow,
    currentIndex,
    allRelaxations,
  ]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        py: 4,
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(135deg, #e0f2f1 0%, #f3e5f5 100%)",
      }}
    >
      <Container maxWidth="md" sx={{ display: "flex", justifyContent: "center" }}>
        <Fade in timeout={800}>
          <Paper
            elevation={6}
            sx={{
              width: "100%",
              borderRadius: 5,
              overflow: "hidden",
              backgroundColor: "rgba(255, 255, 255, 0.6)",
              backdropFilter: "blur(18px)",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ position: "relative", width: "100%", height: { xs: 300, md: 500 } }}>
              <Tooltip title="Go Back">
                <IconButton
                  onClick={() => navigate(-1)}
                  sx={{
                    position: "absolute",
                    top: 16,
                    left: 16,
                    backgroundColor: "rgba(255, 255, 255, 0.75)",
                    zIndex: 10,
                  }}
                >
                  <ArrowBackIosNewRounded />
                </IconButton>
              </Tooltip>

              <video
                ref={videoRef}
                src={videoSrc}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                loop
                muted
                playsInline
              />
            </Box>

            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h5" fontWeight="300" sx={{ mb: 1, color: "#37474f" }}>
                {currentItem?.track_name || "Relaxation Session"}
                {isContinuousFlow && allRelaxations.length > 0 && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({currentIndex + 1} / {allRelaxations.length})
                  </Typography>
                )}
              </Typography>

              <audio
                ref={audioRef}
                src={audioSrc}
                preload="auto"
                crossOrigin="anonymous"
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
              />

              <Box sx={{ width: "85%", mx: "auto", mb: 3 }}>
                <LinearProgress
                  variant="determinate"
                  value={progressPercentage}
                  sx={{
                    height: 6,
                    borderRadius: 5,
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#5c6bc0",
                    },
                  }}
                />
                <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                  <Typography variant="caption">
                    {formatTime(currentTime)}
                  </Typography>
                  <Typography variant="caption">
                    {formatTime(duration)}
                  </Typography>
                </Box>
              </Box>

              <Fab
                size="large"
                onClick={handlePlayPause}
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: "white",
                  color: "#5c6bc0",
                  "&:hover": { transform: "scale(1.08)" },
                  transition: "all 0.28s ease",
                }}
              >
                {isPlaying ? (
                  <PauseRounded sx={{ fontSize: 40 }} />
                ) : (
                  <PlayArrowRounded sx={{ fontSize: 40 }} />
                )}
              </Fab>
            </Box>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default RelaxationTracking;